import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  BookOpen, Film, Tv, Megaphone, Activity, Github,
  Archive, Download, Link2, ChevronDown, Sparkles, Smartphone,
  Send, CalendarClock, Share2, Bell, Cloud, Users, Mail,
  Ban, Eye, UserCog, MessageSquare, ShieldCheck,
} from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/admin/help")({
  head: () => ({
    meta: [
      { title: "دليل لوحة التحكم — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HelpPage,
});

type Section = {
  id: string;
  title: string;
  icon: typeof Film;
  body: React.ReactNode;
};

function Step({ children }: { children: React.ReactNode }) {
  return <li className="leading-relaxed">{children}</li>;
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 text-xs rounded-lg border border-border bg-muted/40 px-3 py-2 leading-relaxed">
      <span className="font-bold">ملاحظة:</span> {children}
    </p>
  );
}

function HelpPage() {
  const { isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>("overview");

  useEffect(() => {
    if (ready && !isAuthed) navigate({ to: "/login" });
  }, [ready, isAuthed, navigate]);

  if (!ready || !isAuthed) return null;

  const sections: Section[] = [
    {
      id: "overview",
      title: "نظرة عامة على لوحة التحكم",
      icon: BookOpen,
      body: (
        <div className="space-y-3 text-sm leading-relaxed">
          <p>لوحة التحكم في <b>Rawan UI</b> مقسّمة إلى قسمين:</p>
          <ol className="list-decimal pr-5 space-y-2">
            <Step><b>تبويبات داخلية</b> في صفحة <Link to="/dashboard" className="text-primary underline">/dashboard</Link>: المسودّات، المحتوى، القصص، الحلقات، الحلقة القادمة، إعلانات المشغّل، إعلانات الموقع، السوشيال، سجل الوصول، إعدادات GitHub، أرشفة التعليقات، النسخ الاحتياطي، إشعارات FCM (مقفلة)، إشعارات Worker، تخزين R2، إعدادات R2، حالة R2.</Step>
            <Step><b>صفحات مستقلة</b> عبر أزرار الشريط العلوي: <b>حساب الأدمن</b> — <b>المستخدمين</b> — <b>التعليقات</b> — <b>رابط محمي</b> — <b>معاينة</b> — <b>الدليل</b> — <b>اتصل بنا</b> — <b>النشرة البريدية</b> — <b>حظر الدول</b>.</Step>
          </ol>
          <Note>الوصول محصور بالإيميلات في <code>ADMIN_EMAILS</code> داخل <code>src/lib/firebase.ts</code>. أي حساب آخر يُعاد توجيهه إلى <code>/login</code>.</Note>
          <Note>معظم الإعدادات تُحفظ في <code>localStorage</code> على متصفّح المدير فقط. لمشاركة التغييرات مع الزوار استخدم دائماً تبويب <b>«إعدادات GitHub»</b> (النشر إلى المستودع).</Note>
        </div>
      ),
    },
    {
      id: "pending",
      title: "المسودّات (Pending) — حفظ العمل قبل النشر",
      icon: Send,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>تبويب <b>«المسودّات»</b> يعرض عدّاداً بجانبه (رقم أحمر) لكل عنصر لم يُنشر بعد.</Step>
          <Step>أي عنصر تبدأ بتعبئته في تبويب <b>المحتوى</b> ولم تضغط <b>«نشر الآن»</b> يُحفظ تلقائياً كمسودّة.</Step>
          <Step>من هنا يمكنك: <b>متابعة التحرير</b>، <b>حذف</b> المسودّة، أو <b>نشرها</b> مباشرة.</Step>
          <Note>المسودّات محلية فقط ولا تُرفع إلى GitHub. لا تعتمد عليها كنسخة احتياطية بعيدة المدى.</Note>
        </ol>
      ),
    },
    {
      id: "add-movie",
      title: "إضافة فيلم / مسلسل جديد (تبويب المحتوى)",
      icon: Film,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/dashboard" className="text-primary underline">لوحة التحكم</Link> ثم اختر تبويب <b>المحتوى</b>.</Step>
          <Step>في نموذج <b>«إضافة فيلم أو مسلسل جديد»</b> اختر <b>النوع</b>: فيلم أو مسلسل (يحدّد المسار <code>/movie/&lt;slug&gt;</code> أو <code>/series/&lt;slug&gt;</code>).</Step>
          <Step>عبّئ العناوين بثلاث لغات: <b>عربي / إنجليزي</b> (إلزامي) و<b>فرنسي</b> (اختياري). اترك حقل <b>slug</b> فارغاً ليُولَّد تلقائياً، أو أدخله بالأحرف الإنجليزية الصغيرة والشرطات فقط.</Step>
          <Step>أدخل <b>السنة، التقييم (0-10)، المدة، المخرج، رابط التريلر (YouTube)</b>.</Step>
          <Step>أضِف <b>البوستر</b> (رأسي للبطاقات) و<b>الخلفية (backdrop)</b> (أفقية لأعلى الصفحة).</Step>
          <Step>عبّئ <b>التصنيفات</b> و<b>طاقم العمل</b> بثلاث لغات مفصولة بفاصلة.</Step>
          <Step>اكتب <b>الملخّص القصير</b> بثلاث لغات (يظهر تحت العنوان).</Step>
          <Step>استخدم <b>محرّر المقال</b> لإضافة فقرات، عناوين، قوائم، تنبيهات، جداول وصور — كل كتلة قابلة للترتيب والحذف.</Step>
          <Step>أضِف <b>روابط التحميل والمشاهدة</b>: لكل سطر اختر <b>الجودة</b> (480p/720p/1080p/4K)، <b>الحجم</b>، <b>السيرفر</b>، ثم الرابط الأصلي مباشرة (يُشفَّر عند العرض).</Step>
          <Step>(اختياري) فعّل شارة <b>«جديد»</b> أو <b>«رائج»</b>.</Step>
          <Step>اضغط <b>المعاينة</b> لرؤية البطاقة، ثم <b>نشر الآن</b>.</Step>
          <Step>القائمة الجانبية <b>«العناصر المنشورة»</b> تعرض كل ما أضفته — أيقونة العين للفتح، السلة للحذف.</Step>
          <Step>للمسلسلات: بعد الحفظ انتقل إلى تبويب <b>الحلقات</b>.</Step>
          <Note>الحفظ في متصفّحك (localStorage). للنشر للجميع استخدم تبويب <b>«إعدادات GitHub»</b> (نشر JSON) أو تبويب <b>«النسخ الاحتياطي»</b> للتصدير.</Note>
        </ol>
      ),
    },
    {
      id: "add-episode",
      title: "إضافة حلقة جديدة (تبويب الحلقات)",
      icon: Tv,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>تأكّد أولاً من إضافة <b>المسلسل</b> نفسه عبر تبويب المحتوى (نوع = مسلسل).</Step>
          <Step>افتح تبويب <b>الحلقات</b> في لوحة التحكم.</Step>
          <Step>اختر المسلسل من القائمة (slug)، ثم حدّد <b>الموسم</b> و<b>رقم الحلقة</b>.</Step>
          <Step>أدخل <b>عنوان الحلقة</b> و(اختياري) <b>الملصق المصغّر (thumbnail)</b>.</Step>
          <Step>الصِق <b>رابط المشاهدة</b> الأصلي — يُشفَّر تلقائياً.</Step>
          <Step>اضغط <b>إضافة الحلقة</b>. تظهر مباشرة في صفحة المسلسل.</Step>
          <Step>لتعديل / حذف: استخدم الجدول أسفل النموذج.</Step>
          <Note>تُعرض في <code>/watch/&lt;slug&gt;/s1e1</code> ويُمرَّر عبر <code>/play</code> بعد فكّ الطبقة الخارجية.</Note>
        </ol>
      ),
    },
    {
      id: "stories",
      title: "إدارة قصص الويب (Web Stories)",
      icon: Sparkles,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إدارة القصص»</b> داخل لوحة التحكم.</Step>
          <Step><b>إضافة قصة</b>: <b>«قصة جديدة»</b> ثم عبّئ <b>slug</b>، <b>العنوان (عربي/إنجليزي)</b>، <b>Poster</b>، وتفاصيل اختيارية (السنة، التقييم، البلد، التصنيفات، الملخّص، طاقم العمل، صورة الخلفية) — كلها تُستعمل لبناء صفحة AMP.</Step>
          <Step><b>الترتيب والظهور</b>: من الجدول يمكنك تحريك القصة للأعلى/الأسفل أو إلى الأول/الآخر، تبديل <b>ظاهرة/مخفية</b>، وتعيين <b>شارة</b> (NEW/HOT/EXCLUSIVE…).</Step>
          <Step><b>مزامنة فورية</b>: أي تعديل يُحفظ محلياً ويُرفع تلقائياً إلى <code>public/data/stories.json</code> على GitHub (شريط «جارٍ المزامنة…»).</Step>
          <Step><b>تعديل قصة</b>: أيقونة القلم لفتح المحرّر الكامل ثم احفظ.</Step>
          <Step><b>حذف قصة</b>: أيقونة السلة تضيفها لقائمة الحذف المعلّقة؛ الحذف الفعلي للملف <code>public/story/&lt;slug&gt;.html</code> يحدث عند <b>«نشر إلى GitHub»</b>.</Step>
          <Step><b>النشر الكامل</b>: <b>«نشر إلى GitHub»</b> يرفع المنيفِست، يُولّد ملفات AMP HTML، وينفّذ الحذف المعلّق. Cloudflare Pages يعيد النشر خلال دقائق.</Step>
          <Note>القصص لا تُخزَّن في Firestore — المصدر الوحيد <code>stories.json</code> و<code>public/story/*.html</code>.</Note>
        </ol>
      ),
    },
    {
      id: "upcoming",
      title: "الحلقة القادمة (شريط عدّ تنازلي)",
      icon: CalendarClock,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«الحلقة القادمة»</b>.</Step>
          <Step>اختر المسلسل من القائمة، ثم أدخل <b>رقم الحلقة</b> و<b>موعد النزول</b> (تاريخ + وقت).</Step>
          <Step>(اختياري) أضِف ملاحظة أو صورة تُعرض بجانب العدّ التنازلي.</Step>
          <Step>اضغط <b>حفظ</b> — يظهر شريط العدّ التنازلي فوراً في صفحة المسلسل وفي الصفحة الرئيسية إن أُشير إليها.</Step>
          <Step>لإخفاء الشريط: اضغط <b>حذف</b> أو ضع تاريخاً في الماضي.</Step>
          <Note>البيانات تُحفظ في <code>content/upcoming-episode.json</code> عبر GitHub (نشر تلقائي).</Note>
        </ol>
      ),
    },
    {
      id: "ads-player",
      title: "إعلانات المشغّل (Player Ads)",
      icon: Megaphone,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إعلانات المشغّل»</b>.</Step>
          <Step>اختر النوع: <b>Pre-roll</b> (قبل الحلقة)، <b>Mid-roll</b> (توقيت مخصّص بالثواني)، أو <b>Overlay</b> (شريط بأسفل المشغّل).</Step>
          <Step>أدخل <b>الرابط المستهدف</b> و<b>الصورة/الفيديو</b> و<b>مدّة العرض</b>.</Step>
          <Step>فعّل/عطّل الإعلان دون حذفه عبر مفتاح <b>«نشط»</b>.</Step>
          <Step>احفظ ثم شاهد أثره فوراً في <code>/play</code>.</Step>
          <Note>الإعلانات محلية للمشغّل فقط ولا تظهر في بقية الصفحات (لتلك استخدم <b>«إعلانات الموقع»</b>).</Note>
        </ol>
      ),
    },
    {
      id: "ads-site",
      title: "إعلانات الموقع (بانرات وفواصل)",
      icon: Megaphone,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إعلانات الموقع»</b>.</Step>
          <Step>لكل مكان (Header, Sidebar, Footer, بين الأقسام) اختر <b>الصورة/HTML</b>، <b>الرابط</b>، و<b>وقت الظهور</b>.</Step>
          <Step>يمكنك تعطيل مكان دون حذف بيانات الإعلان.</Step>
          <Step>احفظ — التغييرات تظهر لجميع الزوار بعد الرفع إلى GitHub.</Step>
          <Note>الإعدادات في <code>content/ad-config.json</code>.</Note>
        </ol>
      ),
    },
    {
      id: "social",
      title: "روابط السوشيال والفوتر",
      icon: Share2,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«روابط السوشيال»</b>.</Step>
          <Step>أدخل روابط منصّاتك (Facebook, X, Instagram, YouTube, TikTok, Telegram…). اترك الحقل فارغاً لإخفاء الأيقونة.</Step>
          <Step>احفظ — تظهر الأيقونات في <b>الفوتر</b> وأي مكوّن يستخدم <code>content/social.json</code>.</Step>
        </ol>
      ),
    },
    {
      id: "logs",
      title: "سجل الوصول (Access Logs)",
      icon: Activity,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«سجل الوصول»</b>.</Step>
          <Step>يعرض آخر محاولات فكّ روابط Safelink: الوقت، الـ slug، النوع، حالة الرابط (صالح/منتهي)، الـ IP المُختصر.</Step>
          <Step>استخدمه للتحقّق من نجاح النشر ولاكتشاف روابط منتهية الصلاحية أو محاولات مشبوهة.</Step>
          <Step>زر <b>مسح السجل</b> يفرّغ السجلّ محلياً فقط.</Step>
        </ol>
      ),
    },
    {
      id: "github",
      title: "إعدادات GitHub — نشر JSON إلى المستودع",
      icon: Github,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إعدادات GitHub»</b>.</Step>
          <Step>أدخل: <b>owner</b> (اسم المستخدم/المؤسّسة)، <b>repo</b> (اسم المستودع)، <b>branch</b> (عادة <code>main</code>)، و<b>Personal Access Token</b> بصلاحية <code>Contents: read &amp; write</code>.</Step>
          <Step>اضغط <b>«اختبار الاتصال»</b> — يجب أن يظهر ✅ مع اسم الفرع.</Step>
          <Step>بعدها كل عملية <b>«نشر إلى GitHub»</b> في التبويبات الأخرى (المحتوى، القصص، الحلقة القادمة، الإعلانات، السوشيال) ستستخدم هذه البيانات.</Step>
          <Note>التوكن يُحفظ في <code>localStorage</code> على متصفّحك فقط، ولا يُرسل لأي خادم — الطلبات مباشرة إلى <code>api.github.com</code>.</Note>
          <Note>لا تشارك المتصفّح مع أشخاص آخرين. يمكنك في أي وقت مسح التوكن بالضغط <b>«حذف الإعدادات»</b>.</Note>
        </ol>
      ),
    },
    {
      id: "comments-archive",
      title: "أرشفة التعليقات (تفريغ Firestore)",
      icon: Archive,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«أرشفة التعليقات»</b>.</Step>
          <Step>تأكّد من إعداد GitHub أوّلاً.</Step>
          <Step>اضغط <b>«جلب كل التعليقات»</b> لتحميلها من Firestore وتجميعها حسب المحتوى.</Step>
          <Step>راجع الملخّص: عدد التعليقات لكل عنصر، أقدم/أحدث تعليق، عدد المستخدمين الفريدين.</Step>
          <Step><b>«تصدير ZIP محلي»</b> لنسخة فورية على جهازك، أو <b>«أرشفة إلى GitHub»</b> لرفعها إلى <code>archives/comments/YYYY-MM/</code>.</Step>
          <Step>بعد التأكّد من نجاح الأرشفة يمكنك <b>«حذف التعليقات الأقدم من …»</b> لتفريغ Firestore.</Step>
          <Note>الحذف نهائي — لا تنفّذه قبل التأكّد من ظهور الأرشيف في GitHub.</Note>
        </ol>
      ),
    },
    {
      id: "backup",
      title: "النسخ الاحتياطي (تصدير/استيراد JSON)",
      icon: Download,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«النسخ الاحتياطي»</b>.</Step>
          <Step><b>تصدير JSON</b> — يُنزَّل ملف يحوي: العناصر المخصّصة، الحلقات، إعدادات الإعلانات، إعدادات GitHub (بدون التوكن)، القصص، السوشيال، الحلقة القادمة.</Step>
          <Step>احفظ الملف في مكان آمن (Drive/GitHub Private).</Step>
          <Step>للاستعادة: <b>استيراد JSON</b> ثم اختر الملف، وسيُسأل عن الدمج أو الاستبدال الكامل.</Step>
          <Step>يُنصح بنسخة أسبوعية على الأقل وقبل أي تحديث كبير.</Step>
          <Note>لا يشمل التعليقات — لتلك استخدم <b>«أرشفة التعليقات»</b>.</Note>
        </ol>
      ),
    },
    {
      id: "fcm",
      title: "إشعارات FCM (Push مباشر للمتصفح)",
      icon: Bell,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>التبويب <b>مقفَل افتراضياً</b>. للفتح: اضغط 7 مرّات متتالية على شعار لوحة التحكم (مفتاح خفي).</Step>
          <Step>بعد الفتح: الصِق <b>Server Key</b> من Firebase Console → Cloud Messaging.</Step>
          <Step>اكتب <b>العنوان</b>، <b>النص</b>، و(اختياري) رابط النقر.</Step>
          <Step>اختر الجمهور: <b>الكلّ</b> أو <b>حسب موضوع (Topic)</b>.</Step>
          <Step>اضغط <b>إرسال</b> — يصل الإشعار لكل من فعّل الاشتراك بالإشعارات.</Step>
          <Note>الاشتراكات مخزّنة في مجموعة <code>fcmTokens</code> في Firestore. راجع القواعد لضمان الوصول الإداري فقط.</Note>
        </ol>
      ),
    },
    {
      id: "notify",
      title: "إشعارات Worker (ربط Cloudflare Worker)",
      icon: Bell,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إشعارات (Worker)»</b>.</Step>
          <Step>الصِق <b>رابط الـ Worker</b> (مثال <code>https://rawan-stream-worker.&lt;account&gt;.workers.dev</code>) و<b>سر <code>RAWAN_NOTIFY_SECRET</code></b>.</Step>
          <Step>اضغط <b>اختبار الاتصال</b> — يجب أن يرد الـ Worker بـ <code>200 OK</code>.</Step>
          <Step>بعدها أي رفع/حذف من تبويب <b>«تخزين R2»</b> يُخطر الـ Worker تلقائياً ليحدّث Firestore.</Step>
          <Note>السر يُحفظ محلياً في المتصفّح ولا يُرسل لأي طرف ثالث. يجب أن يطابق قيمة GitHub Secret نفسها.</Note>
        </ol>
      ),
    },
    {
      id: "r2",
      title: "تخزين R2 — رفع وحذف ملفات الفيديو",
      icon: Cloud,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«تخزين R2»</b>.</Step>
          <Step>اسحب/اختر ملف الفيديو (MP4/HLS). يظهر شريط تقدّم مباشر.</Step>
          <Step>بعد الرفع يظهر <b>مسار الملف</b> داخل الـ Bucket ورابط موقّع للاختبار.</Step>
          <Step>استخدم هذا المسار في نموذج <b>روابط التحميل/المشاهدة</b> داخل تبويب المحتوى (نوع = R2).</Step>
          <Step>الحذف: أيقونة السلة بجانب الملف — يُحذف من R2 ومن Firestore دفعة واحدة عبر Worker.</Step>
          <Note>الرفع يستهلك حصّة Cloudflare — راجع حجم الحصّة الشهرية في لوحة R2.</Note>
        </ol>
      ),
    },
    {
      id: "r2cfg",
      title: "إعدادات R2 (ربط الـ Bucket)",
      icon: Cloud,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«إعدادات R2»</b>.</Step>
          <Step>أدخل: <b>Account ID</b>، <b>Access Key ID</b>، <b>Secret Access Key</b>، و<b>Bucket Name</b> (مثلاً <code>rawan-videos</code>).</Step>
          <Step>(اختياري) <b>Public Base URL</b> إن كنت تستخدم Custom Domain مع R2.</Step>
          <Step>اضغط <b>حفظ + اختبار</b>. يجب أن يظهر عدد الملفات الحالي في الـ Bucket.</Step>
          <Note>هذه المفاتيح تُستعمل من متصفّحك مباشرة عبر مكتبة AWS SDK-compatible — يُفضّل توليد Access Key بصلاحية <b>Object Read/Write</b> فقط.</Note>
        </ol>
      ),
    },
    {
      id: "r2status",
      title: "حالة R2 (استهلاك ومراقبة)",
      icon: Cloud,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح تبويب <b>«حالة R2»</b>.</Step>
          <Step>يعرض: عدد الملفات الحالي، إجمالي الحجم، آخر رفع، وآخر حذف.</Step>
          <Step>يُستخدم للتأكّد من أن Worker متزامن مع الـ Bucket.</Step>
          <Step>اضغط <b>«إعادة المزامنة»</b> إن اختلفت الأرقام بين R2 وFirestore.</Step>
        </ol>
      ),
    },
    {
      id: "users",
      title: "إدارة المستخدمين (/admin/users)",
      icon: Users,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/users" className="text-primary underline">/admin/users</Link>.</Step>
          <Step>يعرض قائمة كل المستخدمين المسجّلين من Firebase Auth: البريد، تاريخ التسجيل، آخر دخول، الحالة (نشط/محظور).</Step>
          <Step>يمكنك: <b>حظر</b> مستخدم (يُمنع من التعليق والدخول)، <b>ترقية</b> إلى مدير مؤقّت (توكن admin)، أو <b>حذف</b> الحساب نهائياً.</Step>
          <Step>ابحث بسرعة عن مستخدم بالبريد أو الـ UID.</Step>
          <Note>لا تحذف حسابك الشخصي أو حسابات الإدارة الرئيسية عن طريق الخطأ.</Note>
        </ol>
      ),
    },
    {
      id: "comments-moderation",
      title: "الإشراف على التعليقات (/admin/comments)",
      icon: MessageSquare,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/comments" className="text-primary underline">/admin/comments</Link>.</Step>
          <Step>تصفّح آخر التعليقات مع اسم صاحبها ورابط المحتوى.</Step>
          <Step>يمكنك: <b>حذف</b> تعليق، <b>حظر</b> صاحبه، أو <b>الرد</b> مباشرة كمدير.</Step>
          <Step>استخدم <b>فلتر الكلمات</b> لعرض التعليقات التي تحوي كلمات محدّدة.</Step>
          <Note>الحذف نهائي في Firestore ولا يمكن التراجع عنه إلا من نسخة أرشيفية.</Note>
        </ol>
      ),
    },
    {
      id: "safelink",
      title: "مولّد الرابط المحمي (/admin/safelink)",
      icon: Link2,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/safelink" className="text-primary underline">صفحة Safelink</Link>.</Step>
          <Step>الصِق الرابط الأصلي (Google Drive، Mega، MediaFire، R2…).</Step>
          <Step>اختر النوع (movie/series/trailer)، أدخل الـ slug، وحدّد مدّة الصلاحية (ساعة، يوم، أسبوع…).</Step>
          <Step>اضغط <b>توليد الرابط المحمي</b> للحصول على رابط <code>/play?t=…</code> بطبقتَي تشفير.</Step>
          <Step>اختبر عبر <Link to="/admin/preview" className="text-primary underline">/admin/preview</Link>: الصِق التوكن، تحقّق من فك التشفير وانتهاء الصلاحية.</Step>
          <Step>انسخ الرابط الناتج فقط. لا تُشارك الرابط الأصلي مطلقاً.</Step>
          <Note>إذا انتهت الصلاحية يظهر للمستخدم خطأ واضح ويُسجَّل في <b>سجل الوصول</b>.</Note>
        </ol>
      ),
    },
    {
      id: "preview",
      title: "معاينة الرابط المحمي (/admin/preview)",
      icon: Eye,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/preview" className="text-primary underline">/admin/preview</Link>.</Step>
          <Step>الصِق توكن <code>t=…</code> من أي رابط مشغّل.</Step>
          <Step>يعرض: نوع المحتوى، الـ slug، رابط المصدر بعد فك الطبقتين، وقت الانتهاء، والوقت المتبقّي.</Step>
          <Step>مفيد للتأكّد من أن الرابط صالح قبل مشاركته أو تضمينه.</Step>
        </ol>
      ),
    },
    {
      id: "contact-admin",
      title: "رسائل «اتصل بنا» (/admin/contact)",
      icon: Mail,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/contact" className="text-primary underline">/admin/contact</Link>.</Step>
          <Step>يعرض الرسائل الواردة عبر نموذج <code>/contact</code> (مزوَّد بواسطة FormSubmit).</Step>
          <Step>عند أوّل إرسال إلى بريد جديد تصلك رسالة تفعيل من FormSubmit (تحقّق من Spam) بها زر <b>Activate</b>.</Step>
          <Step>بعد التفعيل تصل الرسائل تلقائياً إلى البريد الإداري، وتظهر هنا أيضاً كأرشيف.</Step>
        </ol>
      ),
    },
    {
      id: "newsletter",
      title: "النشرة البريدية (/admin/newsletter)",
      icon: Send,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/newsletter" className="text-primary underline">/admin/newsletter</Link>.</Step>
          <Step>يعرض قائمة المشتركين (المسجّلين من الفوتر) — البريد وتاريخ الاشتراك.</Step>
          <Step>يمكنك: <b>تصدير CSV</b> للاستخدام في Mailchimp/Brevo، <b>حذف</b> مشترك، أو <b>إلغاء اشتراك</b> بريد.</Step>
          <Step>البحث السريع بالبريد.</Step>
          <Note>الاشتراكات محفوظة في Firestore ضمن مجموعة النشرة، ومحمية بقواعد الأدمن.</Note>
        </ol>
      ),
    },
    {
      id: "blocked-countries",
      title: "حظر الدول (/admin/blocked-countries)",
      icon: Ban,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/admin/blocked-countries" className="text-primary underline">/admin/blocked-countries</Link>.</Step>
          <Step>اختر الدول التي تريد حظرها من القائمة (رمز ISO ثنائي، مثل <code>US</code>، <code>DE</code>).</Step>
          <Step>يمكنك اختيار <b>نوع الحظر</b>: كامل الموقع، أو المشغّل فقط.</Step>
          <Step>احفظ — يتحقّق الـ Worker من IP الزائر ويعيد صفحة حظر مناسبة.</Step>
          <Note>الاعتماد على GeoIP الخاص بـ Cloudflare — دقّته عالية لكنها ليست 100%.</Note>
        </ol>
      ),
    },
    {
      id: "account",
      title: "حساب الأدمن (/account)",
      icon: UserCog,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>افتح <Link to="/account" className="text-primary underline">/account</Link>.</Step>
          <Step>يمكنك تعديل: <b>الاسم المعروض</b>، <b>الصورة</b>، و<b>كلمة المرور</b>.</Step>
          <Step>يظهر أيضاً: قائمة المفضّلة وقائمة المشاهدة الخاصة بك (localStorage).</Step>
          <Step>زر <b>«تسجيل الخروج»</b> ينهي الجلسة على جميع الأجهزة.</Step>
        </ol>
      ),
    },
    {
      id: "desktop-buttons",
      title: "إخفاء أزرار المشاهدة/التحميل على الحاسوب",
      icon: Smartphone,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step>في صفحات الفيلم والمسلسل، قسم <b>«روابط التحميل والمشاهدة»</b> وأزرار <b>«شاهد / تحميل»</b> داخل كل حلقة <b>لا تظهر</b> على شاشات الحاسوب (≥ 1024px).</Step>
          <Step>تظل ظاهرة بشكل طبيعي على الهاتف والتابلت (&lt; 1024px).</Step>
          <Step>مفروض عبر فئة <code>lg:hidden</code> ولا يحتاج أي إعداد إضافي.</Step>
          <Note>لتغيير هذا السلوك عدّل مكوّنات <code>MovieDownloads</code> و<code>EpisodeItem</code> في <code>src/components/rawan/</code>.</Note>
        </ol>
      ),
    },
    {
      id: "security",
      title: "أمان وأفضل الممارسات",
      icon: ShieldCheck,
      body: (
        <ol className="list-decimal pr-5 space-y-2 text-sm">
          <Step><b>لا تُشارك</b> Personal Access Token أو مفاتيح R2 مع أي شخص، ولا تدفعها إلى Git.</Step>
          <Step>استخدم دائماً متصفّحاً خاصاً بالإدارة على جهازك الشخصي.</Step>
          <Step>راجع تبويب <b>سجل الوصول</b> دورياً للكشف عن محاولات مشبوهة.</Step>
          <Step>خذ <b>نسخة احتياطية</b> كل أسبوع وقبل أي تحديث كبير.</Step>
          <Step>حدّث قائمة <code>ADMIN_EMAILS</code> فور مغادرة أي عضو من الفريق.</Step>
          <Step>لا تعطي أي مستخدم صلاحية <b>admin token</b> إلا لفترة مؤقّتة وبأقلّ صلاحية ممكنة.</Step>
        </ol>
      ),
    },
  ];

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
               style={{ background: "var(--gradient-primary)" }}>
            <BookOpen className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="truncate text-lg sm:text-2xl font-extrabold">دليل لوحة التحكم</h1>
            <p className="text-xs text-muted-foreground truncate">
              مرجع محدَّث لكل تبويب وصفحة إدارية في Rawan UI.
            </p>
          </div>
          <BackToDashboard />
        </div>

        {/* TOC */}
        <nav aria-label="فهرس الدليل" className="mb-6 rounded-2xl border border-border bg-card p-4">
          <h2 className="text-sm font-bold mb-3">المحتويات ({sections.length} قسماً)</h2>
          <ul className="grid gap-2 sm:grid-cols-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}
                   onClick={() => setOpen(s.id)}
                   className="inline-flex items-center gap-2 text-primary hover:underline">
                  <s.icon className="h-4 w-4" /> {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Accordion */}
        <div className="space-y-3">
          {sections.map((s) => {
            const isOpen = open === s.id;
            return (
              <section key={s.id} id={s.id} className="rounded-2xl border border-border bg-card overflow-hidden scroll-mt-20">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-right hover:bg-accent/40 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <span className="font-bold text-base">{s.title}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-border/60">
                    {s.body}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
